// #766: the ONE discriminator that tells artemis which schedule row is the
// first collection, and the ONE guard that lets `appliedDeposit` trust row 0
// as a deposit. Extracted from `appliedDeposit.ts:136-139` when
// `firstCollectionDate.ts` became its second consumer — two hand-maintained
// copies of a three-conjunct guard would drift, and the conjuncts are each
// load-bearing for a reason that is not obvious from the code.
//
// Contract §8 B1/B6: with `has_deposit: true` elemis returns a period-0
// deposit row dated the REQUEST DATE itself; without it, row 0 is instalment 1
// dated request date + 1 month. `schedule[0].number` is `1` either way and
// `num_payments` counts the deposit row, so the row's DATE is the only signal.
//
// Every conjunct is required:
//  - `isDateKey` is what makes the equality MEAN something. `DateKey` is only
//    `string` at runtime, `isValidPaymentSelection` deliberately leaves
//    `scheduleAnchorDate` unchecked (schedule.types.ts:112-114), and the value
//    arrives off a redux-persist rehydration or off wire JSON. Without it, a
//    corrupt pair like anchor `'2026-99-99'` and row date `'2026-99-99'`
//    satisfies the equality — and an equality between two copies of a non-date
//    proves nothing.
//  - `row !== undefined` guards an EMPTY schedule. `ScheduleRow[]` types
//    `schedule[0]` as `ScheduleRow` (`noUncheckedIndexedAccess` is off) so
//    TypeScript does not demand it, but `[][0]` is `undefined` at runtime.
//    Callers rely on a `true` result PROVING `schedule[0]` is defined.
//  - Exact string equality, no tolerance (#767 D5).
import type { ScheduleRow } from '../types/schedule.types';
import { isDateKey, type DateKey } from '../utils/dateKey';

export const isRowZeroOnAnchor = (
    schedule: readonly ScheduleRow[],
    scheduleAnchorDate: DateKey | undefined,
): boolean => {
    const row = schedule[0];
    return isDateKey(scheduleAnchorDate) && row !== undefined && row.date === scheduleAnchorDate;
};
